import React, { useState, useRef } from "react";
import { InfoIcon } from "@/components/icons";
import { PlusIcon } from "@/components/icons/PlusIcon";
import { CheckmarkIcon } from "@/components/icons/CheckmarkIcon";
import { CareIcon } from "@/components/icons/CareIcon";
import { ActionPlanIcon } from "@/components/icons/ActionPlanIcon";
import { ActionBadge } from "@/components/ui/action-badge";
import { Product, formatPrice } from "../product-step/types";
import { BookingLanguage } from "@/types/booking";
import { ProductUspsModal } from "../product-step/card/ProductUSPs";
import { resolveProductKey, t as translate } from "@/locales";
import { useTranslation } from "react-i18next";
import { languageMap } from "@/i18n";
import { useMarket } from "@/hooks/useMarket";

interface MriScanCardProps {
  product: Product;
  isSelected: boolean;
  onToggle: () => void;
  language: BookingLanguage;
  /**
   * Replaces the catalog description. The section hides some packages, which
   * can leave catalog copy referring to a tier the user can't see here.
   */
  descriptionOverride?: string;
}

/**
 * Full-body scan card on the add-ons page — mirrors the package dialog's
 * header: icon left; name, meta line ("Time needed: 60 min · No radiation ·
 * No contrast dye") and price stacked next to it; the description below on
 * the card's full width. Selection affordance matches AddOnCard
 * (circle-plus → filled check).
 */
export const MriScanCard: React.FC<MriScanCardProps> = ({
  product,
  isSelected,
  onToggle,
  language,
  descriptionOverride,
}) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  const { market } = useMarket();
  const [showDetails, setShowDetails] = useState(false);
  const infoClickedRef = useRef(false);

  const displayName =
    product.translations?.name || resolveProductKey(language, product.name);
  const description =
    descriptionOverride ||
    product.translations?.description ||
    resolveProductKey(language, product.description);

  const productImage =
    product.images && product.images.length > 0 ? product.images[0] : null;

  // Safety metadata (no radiation / no contrast dye) — the duration is
  // deliberately left out, it's in the info dialog and only ate space here.
  const safetyChips: string[] = [];
  if (product.radiation_details) {
    const radiationText = resolveProductKey(
      language,
      product.radiation_details as string,
    );
    if (radiationText) safetyChips.push(radiationText);
  } else if (product.contrast_dye === false) {
    safetyChips.push(translate(language, "usp.noRadiation"));
  }
  if (product.contrast_dye === false) {
    safetyChips.push(translate(language, "usp.noContrast"));
  }

  // This card only renders while a membership is in the cart, and that is what
  // earns the member price — on every scan, Core included. Whether the CHF 299
  // is netted out of the scan or billed on top is a separate question, and it
  // belongs to the totals (bundleScanContributionCents), not to this line.
  const originalAmount = product.default_price.unit_amount;
  const currency = product.default_price.currency || "CHF";
  const memberAmount = product.member_price?.unit_amount ?? originalAmount;
  const hasDiscount = memberAmount < originalAmount;

  const handleInfoClick = (event: React.MouseEvent) => {
    infoClickedRef.current = true;
    event.preventDefault();
    event.stopPropagation();
    setShowDetails(true);
    setTimeout(() => {
      infoClickedRef.current = false;
    }, 100);
  };

  return (
    <>
      {/* No h-full here: as the grid item it already stretches to the row
          when the section asks for it (collapsed state), and stays content-
          sized once one card unfolds. */}
      <div
        data-testid="mri-scan-card"
        className="group relative cursor-pointer hover:z-10"
        onClick={() => {
          if (!infoClickedRef.current) onToggle();
        }}
      >
        <div
          aria-hidden
          className="absolute inset-0 rounded-2xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-out shadow-card-lift"
        />
        {/* pb-5 over the uniform p-4: the tag row now closes the card, and a
            bordered pill sitting 16px off the edge crowds it in a way a
            paragraph's descender never did. */}
        <div
          className={`relative h-full rounded-2xl border-[1.6px] shadow-card-sm bg-white p-4 pb-5 ${
            isSelected ? "border-aubergine-800" : "border-transparent"
          }`}
        >
          {/* Header: icon + (name / meta / price), selection icon right */}
          <div className="flex items-start gap-4">
            <div className="w-[88px] h-[88px] bg-neutral-100 rounded-[8px] flex-shrink-0 overflow-hidden">
              {productImage && (
                <img
                  src={productImage}
                  alt={displayName}
                  className="w-full h-full object-cover"
                />
              )}
            </div>

            {/* One gap for all three rows. It used to be gap-0.5 plus an
                extra mt-1 on the price, so the title/meta boundary sat at 2px
                and meta/price at 6px. 4px is the average of the two, so the
                stack keeps its height and the rhythm evens out — the three
                line-heights differ by under half a pixel of half-leading, so
                a uniform gap reads uniform here. */}
            <div className="flex-grow min-w-0 flex flex-col gap-1">
              <h3 className="text-xl font-medium text-neutral-900 leading-tight">
                <span className="whitespace-nowrap">
                  {displayName}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={handleInfoClick}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        handleInfoClick(e as unknown as React.MouseEvent);
                    }}
                    aria-label="Show scan details"
                    className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-neutral-100 transition-colors align-middle cursor-pointer relative -top-px"
                  >
                    <InfoIcon size={16} />
                  </span>
                </span>
              </h3>

              {safetyChips.length > 0 && (
                <p className="text-sm text-neutral-500 leading-tight">
                  {safetyChips.join(" · ")}
                </p>
              )}

              <p className="leading-tight">
                <span className="text-sm text-neutral-600 uppercase">
                  {currency.toUpperCase()}
                </span>{" "}
                {hasDiscount && (
                  <span className="text-sm text-neutral-500 line-through">
                    {formatPrice(originalAmount, market, true)}
                  </span>
                )}{" "}
                <span className="text-lg font-bold text-neutral-900">
                  {formatPrice(
                    hasDiscount ? memberAmount : originalAmount,
                    market,
                    true,
                  )}
                </span>
              </p>
            </div>

            {/* CTA parked in the card's top-right corner — `self-start`
                inside the items-start header row puts it at the same 16px
                inset the card's p-4 gives on the right, so the two gaps
                match without absolute positioning. The card itself is the
                click target, so this is an affordance, not a nested button;
                a <span> also dodges index.css's global uppercase on
                <button>. The label drops below sm, where the header has no
                room for it.

                Outline while it's an offer — hairline border in the same
                aubergine as the plus stroke and the label, so the three read
                as one mark. Filling it on select is what makes the state
                change legible at a glance across two side-by-side cards. */}
            <div className="flex-shrink-0 self-start">
              <span
                className={`inline-flex h-10 min-w-[104px] items-center justify-center gap-1.5 rounded-full border px-3.5 normal-case sm:px-4 ${
                  isSelected
                    ? "border-aubergine-800 bg-aubergine-800 text-white"
                    : "border-aubergine-800 text-aubergine-800"
                }`}
              >
                {isSelected ? (
                  <CheckmarkIcon className="size-4" />
                ) : (
                  <PlusIcon size={16} strokeWidth={1.5} />
                )}
                <span className="hidden text-sm font-medium sm:inline">
                  {isSelected ? t("addOnsStep.mriScanAdded") : t("common.add")}
                </span>
              </span>
            </div>
          </div>

          {/* Description first: it's what the scan actually is. The tags
              below are the service around it, so they read as the footnote
              rather than gating the sentence that sells the package. */}
          {description && (
            <p className="mt-5 text-sm text-neutral-600 leading-snug line-clamp-3">
              {description}
            </p>
          )}

          {/* The same three reassurance points PackageCard and MembershipCard
              carry, as DS ActionBadge tags. The badge owns the label
              typography (12px medium, leading-4, 0.1px tracking), so the three
              stay identical instead of each drifting. Outlined/neutral
              variant — this is supporting detail, not a status.

              Ordered by what a member is most likely to care about: the doctor
              first, then what they get out of it, then who stands behind it.
              The catalog strings are authored with a line break for the
              stacked layout; collapse it, a tag is a single line. */}
          <div className="mt-4 flex flex-wrap gap-2">
            {[
              {
                label: t("packageCard.videoConsultWithDoctor"),
                // The one that isn't an idea but a person — her face carries
                // it better than any glyph would.
                icon: (
                  <img
                    src="/advisory/erat.webp"
                    alt=""
                    className="size-4 rounded-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ),
              },
              {
                label: t("packageCard.personalActionPlan"),
                // Its own default is a fixed grey; let it take the tag's.
                icon: (
                  <ActionPlanIcon size={16} strokeWidth={1.9} className="" />
                ),
              },
              {
                label: t("packageCard.insightsBySwissPhysicians"),
                icon: <CareIcon size={16} strokeWidth={2} />,
              },
            ].map(({ label, icon }) => (
              <ActionBadge
                key={label}
                label={label.replace(/\s*\n\s*/g, " ")}
                icon={icon}
                className="text-neutral-700"
              />
            ))}
          </div>
        </div>
      </div>

      <ProductUspsModal
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        product={product}
        productId={product.id}
        productName={displayName}
        language={language}
        useCase="package_dialog_content"
        onSelectAndContinue={() => {
          if (!isSelected) onToggle();
          setShowDetails(false);
        }}
      />
    </>
  );
};
